import { useMemo, type ReactNode } from 'react'
import {
  DndContext,
  DragOverlay,
  closestCorners,
  useDroppable,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { Skeleton } from '@/components/ui/skeleton'
import { IssueCard } from './issue-card'
import { cn } from '@/lib/utils'
import type { WorkIssue } from '@/types/github'
import { dragIssue } from './drag-data'
import { SortableIssue } from './dnd-shared'
import { useDragHarness } from './use-drag-harness'

export type BoardColumn = {
  key: string
  title: string
  /** Rendered before the title (StageIcon / source icon / priority dot). */
  glyph?: ReactNode
}

export type DropPosition = {
  toColumn: string
  above: WorkIssue | null
  below: WorkIssue | null
  /** Target order of the destination column ids, including the moved card. */
  orderedIds: string[]
}

export type BoardProps = {
  columns: BoardColumn[]
  itemsByColumn: Map<string, WorkIssue[]>
  onCardClick: (issue: WorkIssue) => void
  /** Absent → cross-column drag disabled (Triage is click-only). */
  onMoveAcross?: (issue: WorkIssue, pos: DropPosition) => void
  /** Absent → within-column reorder disabled. */
  onReorder?: (issue: WorkIssue, pos: DropPosition) => void
  showEffort?: boolean
  /** This board's own order context, so cards don't flag themselves. */
  excludeContext?: string
}

const COLUMN_PREFIX = 'column:'


function Column({
  column,
  issues,
  dragDisabled,
  onCardClick,
  showEffort,
  excludeContext,
}: {
  column: BoardColumn
  issues: WorkIssue[]
  dragDisabled: boolean
  onCardClick: (issue: WorkIssue) => void
  showEffort?: boolean
  excludeContext?: string
}) {
  const { setNodeRef, isOver } = useDroppable({ id: COLUMN_PREFIX + column.key })
  return (
    <div
      ref={setNodeRef}
      className={cn(
        'bg-surface border-border flex min-w-[300px] max-w-[520px] flex-1 flex-col rounded-xl border',
        isOver && 'border-[color-mix(in_oklch,var(--primary)_45%,var(--border))]',
      )}
    >
      <div className="flex items-center gap-1.5 px-2.5 pt-2 pb-1.5 text-[12.5px] font-semibold">
        {column.glyph}
        <span className="truncate">{column.title}</span>
        <span className="text-faint-foreground font-mono text-[11px] font-medium">
          {issues.length}
        </span>
      </div>
      <div className="scroll-y min-h-[60px] flex-1 px-2 pb-2">
        <SortableContext
          items={issues.map((i) => i.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="flex flex-col gap-[7px]">
            {issues.map((issue) => (
              <SortableIssue
                key={issue.id}
                issue={issue}
                disabled={dragDisabled}
                onOpen={onCardClick}
                showEffort={showEffort}
                excludeContext={excludeContext}
              />
            ))}
            {issues.length === 0 && (
              <div className="border-border-strong text-faint-foreground rounded-lg border border-dashed p-4 text-center text-xs">
                No items
              </div>
            )}
          </div>
        </SortableContext>
      </div>
    </div>
  )
}

export function Board({
  columns,
  itemsByColumn,
  onCardClick,
  onMoveAcross,
  onReorder,
  showEffort,
  excludeContext,
}: BoardProps) {
  const dragDisabled = !onMoveAcross && !onReorder
  const { active, setActive, sensors, openGuarded, markDragEnded } = useDragHarness(onCardClick)

  const columnOf = useMemo(() => {
    const map = new Map<string, string>()
    for (const [key, issues] of itemsByColumn) {
      for (const issue of issues) map.set(issue.id, key)
    }
    return map
  }, [itemsByColumn])

  function findColumnName(issueId: string): string | undefined {
    return columnOf.get(issueId)
  }

  function resolveDrop(event: DragEndEvent): { issue: WorkIssue; pos: DropPosition } | null {
    const issue = dragIssue(event.active)
    const overId = event.over?.id
    if (!issue || overId === undefined) return null

    const fromColumn = columnOf.get(issue.id)
    const toColumn =
      typeof overId === 'string' && overId.startsWith(COLUMN_PREFIX)
        ? overId.slice(COLUMN_PREFIX.length)
        : columnOf.get(String(overId))
    if (!toColumn || fromColumn === undefined) return null

    const target = (itemsByColumn.get(toColumn) ?? []).filter((i) => i.id !== issue.id)

    let insertAt = target.length
    if (typeof overId === 'string' && !overId.startsWith(COLUMN_PREFIX)) {
      const overIndex = target.findIndex((i) => i.id === overId)
      if (overIndex !== -1) {
        const source = itemsByColumn.get(fromColumn) ?? []
        const fromIndex = source.findIndex((i) => i.id === issue.id)
        const movingDownSameColumn =
          fromColumn === toColumn && fromIndex !== -1 && fromIndex <= overIndex
        insertAt = movingDownSameColumn ? overIndex + 1 : overIndex
      }
    }

    const orderedIds = target.map((i) => i.id)
    orderedIds.splice(insertAt, 0, issue.id)

    return {
      issue,
      pos: {
        toColumn,
        above: target[insertAt - 1] ?? null,
        below: target[insertAt] ?? null,
        orderedIds,
      },
    }
  }

  function handleDragEnd(event: DragEndEvent) {
    setActive(null)
    markDragEnded()
    const drop = resolveDrop(event)
    if (!drop) return
    const fromColumn = columnOf.get(drop.issue.id)
    if (drop.pos.toColumn === fromColumn) {
      onReorder?.(drop.issue, drop.pos)
    } else {
      onMoveAcross?.(drop.issue, drop.pos)
    }
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={(e: DragStartEvent) => setActive(dragIssue(e.active) ?? null)}
      onDragCancel={() => setActive(null)}
      onDragEnd={handleDragEnd}
      accessibility={{
        screenReaderInstructions: {
          draggable: 'Use arrow keys to move. Space or Enter to drop. Escape to cancel.',
        },
        announcements: {
          onDragStart: ({ active }) =>
            `Picked up #${dragIssue(active)?.number} from ${findColumnName(String(active.id)) ?? 'the board'}.`,
          onDragOver: ({ active, over }) =>
            over
              ? `#${dragIssue(active)?.number} is over ${
                  typeof over.id === 'string' && over.id.startsWith(COLUMN_PREFIX)
                    ? over.id.slice(COLUMN_PREFIX.length)
                    : findColumnName(String(over.id)) ?? 'a card'
                }.`
              : `#${dragIssue(active)?.number} is over an empty area.`,
          onDragEnd: ({ active, over }) =>
            over
              ? `#${dragIssue(active)?.number} dropped.`
              : `Drop cancelled for #${dragIssue(active)?.number}.`,
          onDragCancel: ({ active }) =>
            `Drag cancelled for #${dragIssue(active)?.number}.`,
        },
      }}
    >
      <div className="flex h-full gap-3 overflow-x-auto p-4">
        {columns.map((column) => (
          <Column
            key={column.key}
            column={column}
            issues={itemsByColumn.get(column.key) ?? []}
            dragDisabled={dragDisabled}
            onCardClick={openGuarded}
            showEffort={showEffort}
            excludeContext={excludeContext}
          />
        ))}
      </div>
      <DragOverlay>
        {active && (
          <div className="rotate-2 shadow-[var(--shadow-drag)]">
            <IssueCard issue={active} showEffort={showEffort} excludeContext={excludeContext} />
          </div>
        )}
      </DragOverlay>
    </DndContext>
  )
}

export function BoardSkeleton({ columns = 4 }: { columns?: number }) {
  return (
    <div className="flex h-full gap-3 overflow-hidden p-4">
      {Array.from({ length: columns }, (_, c) => (
        <div key={c} className="bg-surface border-border min-w-[300px] max-w-[520px] flex-1 rounded-xl border p-2">
          <Skeleton className="mb-2 h-4 w-24" />
          <div className="flex flex-col gap-[7px]">
            {Array.from({ length: 3 }, (_, r) => (
              <Skeleton key={r} className="h-24 w-full" />
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}
